import AppComponent from '../base/app-component.ts';
import type { DataTable, ColumnDef } from './data-table.ts';
import { formatNumero, buildExportQuery, scaricaFile } from '../lib/utils.ts';
import { raccogliDateFilters, handleDateOpChange, resetDateRanges } from '../lib/date-filter.ts';

const COLUMNS: ColumnDef[] = [
  { key: 'codiceFiscale', label: 'Codice Fiscale', type: 'code', sortable: true },
  { key: 'dataNascita', label: 'Data Nascita', type: 'text', sortable: true },
  { key: 'cognome', label: 'Cognome', type: 'text', sortable: true },
  { key: 'nome', label: 'Nome', type: 'text', sortable: true },
  { key: 'telefonoLabel', label: 'Telefono', type: 'text', emptyText: '-' },
  { key: 'ce', label: 'CE', type: 'text' },
  { key: 'ec', label: 'EC', type: 'text' },
  { key: '_azioni', label: '', type: 'text' },
];

export class TabellaLavoratori extends AppComponent {
  static override componentName = 'wc-tabella-lavoratori';
  protected override get templateId() { return 'tabella-lavoratori'; }

  private dataTable: DataTable | null = null;
  private currentSort: string | null = null;
  private currentSortDir: 'asc' | 'desc' | null = null;
  private currentFilter = '';
  private currentPage = 1;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private offcanvas: any = null;
  private modals: { dispose(): void }[] = [];

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      ...super.buildRenderingData(),
      columnsJson: JSON.stringify(COLUMNS),
    };
  }

  protected override afterRender(): void {
    this.dataTable = this.$('wc-data-table') as DataTable | null;
    if (!this.dataTable) return;

    this.setupTableEvents();
    this.setupFiltriAvanzati();
    this.setupFiltriRapidi();
    this.setupExportExcel();
    this.setupEditLavoratore();
    this.caricaLavoratori();
  }

  private setupExportExcel(): void {
    this.delegate.on('click', '[data-action="export-excel"]', (e: Event) => {
      e.preventDefault();
      const qs = buildExportQuery(this.raccogliFiltri(), this.currentFilter);
      scaricaFile(`/api/lavoratori/export-csv?${qs}`);
    });
  }

  private setupTableEvents(): void {
    if (!this.dataTable) return;

    this.listen(this.dataTable, 'dt-page-change', (e: Event) => {
      this.currentPage = (e as CustomEvent).detail.page;
      this.caricaLavoratori();
    });

    this.listen(this.dataTable, 'dt-sort-change', (e: Event) => {
      const { sort, dir } = (e as CustomEvent).detail;
      this.currentSort = sort;
      this.currentSortDir = dir;
      this.currentPage = 1;
      this.caricaLavoratori();
    });

    this.listen(this.dataTable, 'dt-filter-change', (e: Event) => {
      this.currentFilter = (e as CustomEvent).detail.filter;
      this.currentPage = 1;
      this.caricaLavoratori();
    });

    this.listen(this.dataTable, 'dt-row-click', (e: Event) => {
      const { row } = (e as CustomEvent).detail;
      window.location.href = `/lavoratori/${row.id}`;
    });
    
    this.listen(this.dataTable, 'dt-row-action', (e: Event) => {
      const { action, row } = (e as CustomEvent).detail;
      if (action === 'modifica-lavoratore') {
        const id = Number(row.id);
        
        if (!id) return;
        const comp = document.querySelector('#modalEditLavoratore wc-edit-lavoratore') as HTMLElement & { edit(id: number): void } | null;
        if (comp) comp.edit(id);
        this.getModal('#modalEditLavoratore')?.show();
      }
    });
  }

  private setupFiltriAvanzati(): void {
    const offcanvasEl = this.$('[data-offcanvas="filtri"]');
    if (offcanvasEl) {
      this.offcanvas = new (globalThis as any).bootstrap.Offcanvas(offcanvasEl);
    }

    this.delegate.on('click', '[data-action="apri-filtri"]', () => {
      this.offcanvas?.show();
    });

    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    if (form) {
      this.listen(form, 'submit', (e: Event) => {
        e.preventDefault();
        this.offcanvas?.hide();
        this.syncButtonGroup('sindacato');
        this.syncButtonGroup('cassa');
        this.currentPage = 1;
        this.caricaLavoratori();
      });
    }

    this.delegate.on('click', '[data-action="reset-filtri"], [data-action="reset-filtri-toolbar"]', () => {
      this.resetFiltri();
      this.offcanvas?.hide();
      this.currentPage = 1;
      this.caricaLavoratori();
    });

    this.delegate.on('change', '[data-date-op]', (e: Event) => handleDateOpChange(this, e));
  }

  private setupFiltriRapidi(): void {
    this.delegate.on('click', '[data-filtro-rapido] button', (e: Event) => {
      const btn = (e.target as HTMLElement).closest('button') as HTMLElement;
      const group = btn.closest('[data-filtro-rapido]') as HTMLElement;
      const campo = group.dataset.filtroRapido!;
      const value = btn.dataset.value!;
      const wasActive = btn.classList.contains('active');

      group.querySelectorAll('button').forEach(b => b.classList.remove('active'));
      if (!wasActive) btn.classList.add('active');

      const select = this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null;
      if (select) select.value = wasActive ? '' : value;

      this.currentPage = 1;
      this.caricaLavoratori();
    });
  }

  private syncButtonGroup(campo: string): void {
    const select = this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null;
    const group = this.$(`[data-filtro-rapido="${campo}"]`);
    if (!select || !group) return;
    const val = select.value;
    group.querySelectorAll('button').forEach(b => {
      const btn = b as HTMLElement;
      btn.classList.toggle('active', btn.dataset.value === val);
    });
  }

  private raccogliFiltri(): Record<string, string> {
    const params: Record<string, string> = {};

    const testoFields = ['cognome', 'nome', 'codiceFiscale', 'comuneNascita', 'comuneResidenza', 'viaResidenza', 'codce'];
    for (const campo of testoFields) {
      const val = (this.$(`[data-filtro-val="${campo}"]`) as HTMLInputElement | null)?.value?.trim();
      const op = (this.$(`[data-filtro-op="${campo}"]`) as HTMLSelectElement | null)?.value;
      if (val) {
        params[campo] = val;
        if (op) params[`${campo}Op`] = op;
      }
    }

    for (const campo of ['cassa', 'sindacato', 'statoLavoratore']) {
      const val = (this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null)?.value;
      if (val) params[campo] = val;
    }

    Object.assign(params, raccogliDateFilters(this, ['dataNascita']));

    return params;
  }

  private resetFiltri(): void {
    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    form?.reset();
    resetDateRanges(this);
    this.syncButtonGroup('sindacato');
    this.syncButtonGroup('cassa');
  }

  private updateFiltriCount(): void {
    const filtri = this.raccogliFiltri();
    const count = Object.keys(filtri).filter(k => !k.endsWith('Op')).length;
    const badge = this.$('[data-filtri-count]');
    if (badge) {
      badge.textContent = count > 0 ? String(count) : '';
      badge.classList.toggle('d-none', count === 0);
    }
    const resetBtn = this.$('[data-action="reset-filtri-toolbar"]');
    resetBtn?.classList.toggle('d-none', count === 0);
  }

  private async caricaLavoratori(): Promise<void> {
    if (!this.dataTable) return;
    this.dataTable.setLoading();

    const filtri = this.raccogliFiltri();
    this.updateFiltriCount();

    const result = await this.api.lavoratori.lista({
      ...filtri,
      page: this.currentPage,
      limit: 30,
      sort: this.currentSort ?? undefined,
      sortDir: this.currentSortDir ?? undefined,
      q: this.currentFilter || undefined,
    });

    if (!result.ok) {
      this.dataTable.setError(result.error);
      return;
    }

    const { lavoratori, count, totalPages, pageSize, page } = result.data;
    const rows = lavoratori.map(l => {
      const raw = l as Record<string, unknown>;
      return {
        ...raw,
        telefonoLabel: raw.cellulare || raw.telefono || null,
      };
    });
    this.dataTable.setData(rows, {
      page, totalPages, count, pageSize,
    });
    this.updateTotale(count);
  }

  private updateTotale(count: number): void {
    const el = document.querySelector('[data-totale-lavoratori]');
    if (el) el.textContent = formatNumero(count) + ' risultati';
  }

  // --- Edit Lavoratore ---

  private setupEditLavoratore(): void {
    // Sposta modale a body
    const modalEl = this.$('[data-modal="edit-lavoratore"]');
    if (modalEl) document.body.appendChild(modalEl);

    // Nuovo lavoratore
    this.delegate.on('click', '[data-action="nuovo-lavoratore"]', () => {
      const comp = document.querySelector('#modalEditLavoratore wc-edit-lavoratore') as HTMLElement & { reset(): void } | null;
      if (comp) {
        comp.removeAttribute('lavoratore-id');
        comp.reset();
      }
      this.getModal('#modalEditLavoratore')?.show();
    });

    // Evento salvato → chiudi modale e ricarica
    this.listen(document, 'lavoratore-salvato', ((e: CustomEvent) => {
      this.getModal('#modalEditLavoratore')?.hide();
      if (e.detail.isNew && e.detail.lavoratore?.codiceFiscale) {
        this.currentFilter = e.detail.lavoratore.codiceFiscale;
        const input = this.dataTable?.querySelector('[data-dt-filter-input]') as HTMLInputElement | null;
        if (input) input.value = this.currentFilter;
      }
      this.currentPage = 1;
      this.caricaLavoratori();
    }) as EventListener);
  }

  private getModal(selector: string): { show(): void; hide(): void; dispose(): void } | null {
    const el = document.querySelector(selector);
    if (!el) return null;
    const bs = (globalThis as any).bootstrap;
    const existing = bs?.Modal?.getInstance(el);
    if (existing) return existing;
    const m = new bs.Modal(el);
    this.modals.push(m);
    return m;
  }

  protected override onDisconnected(): void {
    this.offcanvas?.dispose();
    for (const m of this.modals) {
      try { m.dispose(); } catch { /* ignore */ }
    }
    const modale = document.querySelector('[data-modal="edit-lavoratore"]');
    if (modale) modale.remove();
  }
}
